import {useCallback, useEffect, useMemo, useState} from 'react'
import Swal from 'sweetalert2'
import CustomSelect from '../../modules/custom-select/CustomSelect'
import type {ManualExamStatus} from '../my-course-exam/manual/core/_models'
import type {
  ManualExamSharedCourseGroup,
  ManualExamSharedCourseItem,
  ManualExamSharedCourseOption,
} from './core/_models'
import {
  deleteManualExamSharedCourse,
  getManualExamSharedCourses,
  saveManualExamSharedCourses,
} from './core/_requests'

type ManualExamSharedCoursesModalProps = {
  examId: number
  isOpen: boolean
  onClose: () => void
  onChanged?: () => void | Promise<void>
  statusLabels: Record<ManualExamStatus, string>
}

type SelectOption = {
  value: number
  label: string
  data: {
    courseName?: string | null
    courseCode?: string | null
    instructorName?: string | null
    semesterCourseName?: string | null
  }
}

type OrganizationOption = {
  value: number
  label: string
}

const formatCourseLabel = (
  courseName?: string | null,
  courseCode?: string | null,
  instructorName?: string | null,
  semesterCourseName?: string | null
) => {
  const parts: string[] = []
  const trimmedName = courseName?.trim()
  const trimmedCode = courseCode?.trim()
  const trimmedInstructor = instructorName?.trim()
  const trimmedSemesterCourseName = semesterCourseName?.trim()

  if (trimmedCode && trimmedName) {
    parts.push(`${trimmedCode} - ${trimmedName}`)
  } else if (trimmedName) {
    parts.push(trimmedName)
  } else if (trimmedCode) {
    parts.push(trimmedCode)
  }

  if (trimmedInstructor) {
    parts.push(trimmedInstructor)
  }

  if (trimmedSemesterCourseName) {
    parts.push(trimmedSemesterCourseName)
  }
  if (parts.length === 0) {
    return 'Ders'
  }

  return parts.join(' / ')
}

const ManualExamSharedCoursesModal: React.FC<ManualExamSharedCoursesModalProps> = ({
  examId,
  isOpen,
  onClose,
  onChanged,
  statusLabels,
}) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [group, setGroup] = useState<ManualExamSharedCourseGroup | null>(null)
  const [currentSelectionIds, setCurrentSelectionIds] = useState<number[]>([])
  const [queuedCourseIds, setQueuedCourseIds] = useState<number[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null)

  const loadData = useCallback(async () => {
    if (!examId) {
      return
    }

    try {
      setLoading(true)
      const response = await getManualExamSharedCourses(examId)
      setGroup(response)
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'Ortak sınav bilgileri yüklenemedi.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setLoading(false)
    }
  }, [examId])

  useEffect(() => {
    if (isOpen) {
      setCurrentSelectionIds([])
      setQueuedCourseIds([])
      setSelectedOrganizationId(null)
      loadData()
    }
  }, [isOpen, loadData])

  useEffect(() => {
    if (!group?.availableCourses || group.availableCourses.length === 0) {
      setQueuedCourseIds([])
      return
    }

    const allowedIds = new Set(group.availableCourses.map((course) => course.semesterCourseId))

    setQueuedCourseIds((prev) => prev.filter((id) => allowedIds.has(id)))
  }, [group])

  const organizationOptions = useMemo<OrganizationOption[]>(() => {
    if (!group?.availableCourses) {
      return []
    }

    const organizationMap = new Map<number, string>()

    group.availableCourses.forEach((course) => {
      if (course.organizationId === undefined || course.organizationId === null) {
        return
      }

      if (organizationMap.has(course.organizationId)) {
        return
      }

      const hierarchyName = course.organizationHierarchyName?.trim()
      const organizationName = course.organizationName?.trim()
      const label = hierarchyName + ' / ' + organizationName || 'Birim'

      organizationMap.set(course.organizationId, label)
    })

    return Array.from(organizationMap.entries())
      .map(([value, label]) => ({value, label}))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr', {sensitivity: 'base'}))
  }, [group])

  useEffect(() => {
    if (organizationOptions.length === 0) {
      setSelectedOrganizationId(null)
      return
    }

    if (selectedOrganizationId !== null) {
      const exists = organizationOptions.some((option) => option.value === selectedOrganizationId)
      if (!exists) {
        setSelectedOrganizationId(organizationOptions.length === 1 ? organizationOptions[0].value : null)
      }
      return
    }

    if (organizationOptions.length === 1) {
      setSelectedOrganizationId(organizationOptions[0].value)
    }
  }, [organizationOptions, selectedOrganizationId])

  const selectOptions = useMemo<SelectOption[]>(() => {
    if (!group?.availableCourses) {
      return []
    }

    const hasOrganizationFilter = organizationOptions.length > 0

    const filteredCourses = group.availableCourses.filter((course) => {
      if (!hasOrganizationFilter) {
        return true
      }

      if (selectedOrganizationId === null) {
        return false
      }

      return course.organizationId === selectedOrganizationId
    })

    return filteredCourses.map((course) => ({
      value: course.semesterCourseId,
      label: formatCourseLabel(
        course.courseName,
        course.courseCode,
        course.instructorName,
        course.semesterCourseName
      ),
      data: {
        courseName: course.courseName,
        courseCode: course.courseCode,
        instructorName: course.instructorName,
        semesterCourseName: course.semesterCourseName,
      },
    }))
  }, [group, organizationOptions, selectedOrganizationId])

  useEffect(() => {
    setCurrentSelectionIds((prev) => {
      if (prev.length === 0) {
        return prev
      }

      const allowedCourseIds = new Set(selectOptions.map((option) => option.value))
      const filtered = prev.filter((id) => allowedCourseIds.has(id))

      return filtered.length === prev.length ? prev : filtered
    })
  }, [selectOptions])

  const selectedOrganizationOption = useMemo(() => {
    return organizationOptions.find((option) => option.value === selectedOrganizationId) ?? null
  }, [organizationOptions, selectedOrganizationId])

  const currentSelectedOptions = useMemo(() => {
    return selectOptions.filter((option) => currentSelectionIds.includes(option.value))
  }, [selectOptions, currentSelectionIds])

  const queuedCourses = useMemo(() => {
    if (!group?.availableCourses) {
      return []
    }

    const optionMap = new Map<number, ManualExamSharedCourseOption>()
    group.availableCourses.forEach((course) => {
      optionMap.set(course.semesterCourseId, course)
    })

    return queuedCourseIds
      .map((id) => optionMap.get(id))
      .filter((item): item is ManualExamSharedCourseOption => Boolean(item))
  }, [group, queuedCourseIds])

  const handleOrganizationChange = useCallback((option: any) => {
    if (!option) {
      setSelectedOrganizationId(null)
      setCurrentSelectionIds([])
      return
    }

    const parsedValue = Number(option.value)
    setSelectedOrganizationId(Number.isFinite(parsedValue) ? parsedValue : null)
    setCurrentSelectionIds([])
  }, [])

  const handleSelectChange = useCallback((options: any) => {
    if (!options) {
      setCurrentSelectionIds([])
      return
    }

    if (Array.isArray(options)) {
      const values = options.map((option: any) => Number(option.value)).filter((value) => Number.isFinite(value))
      setCurrentSelectionIds(values)
      return
    }

    const value = Number(options.value)
    setCurrentSelectionIds(Number.isFinite(value) ? [value] : [])
  }, [])

  const handleAddToQueue = useCallback(() => {
    if (currentSelectionIds.length === 0) {
      Swal.fire('Uyarı', 'Listeye eklemek için ders seçmelisiniz.', 'info')
      return
    }

    const existing = new Set(queuedCourseIds)
    let changed = false

    currentSelectionIds.forEach((id) => {
      if (!existing.has(id)) {
        existing.add(id)
        changed = true
      }
    })

    if (!changed) {
      Swal.fire('Bilgi', 'Seçilen dersler zaten listede bulunuyor.', 'info')
      return
    }

    setQueuedCourseIds(Array.from(existing))
    setCurrentSelectionIds([])
    Swal.fire('Başarılı', 'Seçilen dersler listeye eklendi.', 'success')
  }, [currentSelectionIds, queuedCourseIds])

  const handleRemoveQueuedCourse = useCallback((semesterCourseId: number) => {
    setQueuedCourseIds((prev) => prev.filter((id) => id !== semesterCourseId))
  }, [])

  const refreshAndNotifyParent = useCallback(async () => {
    await loadData()
    await Promise.resolve(onChanged?.())
  }, [loadData, onChanged])

  const handleSave = useCallback(async () => {
    if (!examId || queuedCourseIds.length === 0) {
      Swal.fire('Uyarı', 'Listeye en az bir ders eklemelisiniz.', 'info')
      return
    }

    try {
      setSaving(true)
      await saveManualExamSharedCourses({examId, semesterCourseIds: queuedCourseIds})
      Swal.fire('Başarılı', 'Listelenen dersler için sınav kaydı oluşturuldu.', 'success')
      setQueuedCourseIds([])
      setCurrentSelectionIds([])
      await refreshAndNotifyParent()
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'İşlem tamamlanamadı.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setSaving(false)
    }
  }, [examId, queuedCourseIds, refreshAndNotifyParent])

  const handleDelete = useCallback(
    async (item: ManualExamSharedCourseItem) => {
      if (!item || item.isSource) {
        return
      }

      const result = await Swal.fire({
        title: 'Dersi Çıkar',
        text: 'Seçilen ders için oluşturulan sınav kaydı silinecektir. Devam etmek istediğinize emin misiniz?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Evet, sil',
        cancelButtonText: 'Vazgeç',
        reverseButtons: true,
      })

      if (!result.isConfirmed) {
        return
      }

      try {
        setSaving(true)
        await deleteManualExamSharedCourse({examId: item.examId})
        Swal.fire('Başarılı', 'Ders ortak sınav listesinden kaldırıldı.', 'success')
        await refreshAndNotifyParent()
      } catch (err: any) {
        console.error(err)
        const message = err?.response?.data || 'Silme işlemi tamamlanamadı.'
        Swal.fire('Hata', message, 'error')
      } finally {
        setSaving(false)
      }
    },
    [refreshAndNotifyParent]
  )

  if (!isOpen) {
    return null
  }

  const sharedExams = group?.exams ?? []

  return (
    <>
      <div className='modal fade show d-block' tabIndex={-1} role='dialog' aria-modal='true'>
        <div className='modal-dialog modal-lg modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Ortak Sınav Dersleri</h5>
              <button type='button' className='btn-close' aria-label='Kapat' onClick={onClose} disabled={saving} />
            </div>
            <div className='modal-body'>
              {loading ? (
                <div className='text-center py-10'>
                  <span className='spinner-border text-primary' role='status' />
                </div>
              ) : (
                <div className='d-flex flex-column gap-5'>
                  {organizationOptions.length > 0 && (
                    <div>
                      <label className='form-label fw-semibold'>Birim Seçimi</label>
                      <CustomSelect
                        options={organizationOptions}
                        value={selectedOrganizationOption}
                        onChange={handleOrganizationChange}
                        isDisabled={saving || organizationOptions.length === 0}
                        placeholder='Birim seçiniz'
                        classNamePrefix='shared-course-organization-select'
                        isClearable
                      />
                    </div>
                  )}
                  <div>
                    <label className='form-label fw-semibold'>Ders Seçimi</label>
                    <div className='d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-end'>
                      <div className='flex-grow-1'>
                        <CustomSelect
                          isMulti
                          options={selectOptions}
                          value={currentSelectedOptions}
                          onChange={handleSelectChange}
                          closeMenuOnSelect={false}
                          isDisabled={
                            saving ||
                            selectOptions.length === 0 ||
                            (organizationOptions.length > 0 && selectedOrganizationId === null)
                          }
                          placeholder={
                            organizationOptions.length > 0 && selectedOrganizationId === null
                              ? 'Önce birim seçiniz'
                              : selectOptions.length === 0
                              ? 'Uygun ders bulunmuyor'
                              : 'Ders seçiniz'
                          }
                          classNamePrefix='shared-course-select'
                        />
                      </div>
                      <button
                        type='button'
                        className='btn btn-light-primary min-w-150px'
                        onClick={handleAddToQueue}
                        disabled={saving || currentSelectionIds.length === 0}
                      >
                        Listeye Ekle
                      </button>
                    </div>
                    <small className='text-muted d-block mt-2'>
                      Farklı birimlerdeki dersleri sırayla seçip listeye ekleyebilir, ardından tamamını birlikte
                      kaydedebilirsiniz.
                    </small>
                  </div>
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='fw-semibold mb-0'>Kopyalanacak Dersler</h6>
                      <span className='badge badge-light-primary'>{queuedCourses.length}</span>
                    </div>
                    {queuedCourses.length === 0 ? (
                      <div className='alert alert-secondary mb-0'>Listeye henüz ders eklenmedi.</div>
                    ) : (
                      <div className='d-flex flex-column gap-2'>
                        {queuedCourses.map((course) => {
                          const label = formatCourseLabel(
                            course.courseName,
                            course.courseCode,
                            course.instructorName,
                            course.semesterCourseName
                          )

                          return (
                            <div
                              key={`queued-course-${course.semesterCourseId}`}
                              className='border rounded p-3 d-flex flex-column flex-md-row justify-content-between gap-3'
                            >
                              <div>
                                <div className='fw-semibold text-gray-900'>{label}</div>
                                {course.organizationHierarchyName && (
                                  <div className='text-muted small'>{course.organizationHierarchyName}</div>
                                )}
                              </div>
                              <div className='d-flex align-items-center'>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-danger'
                                  onClick={() => handleRemoveQueuedCourse(course.semesterCourseId)}
                                  disabled={saving}
                                >
                                  Listeden Çıkar
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='fw-semibold mb-0'>Ortak Sınav Dersleri</h6>
                      <span className='badge badge-light-primary'>{sharedExams.length}</span>
                    </div>
                    {sharedExams.length === 0 ? (
                      <div className='alert alert-warning mb-0'>Henüz başka ders eklenmemiştir.</div>
                    ) : (
                      <div className='d-flex flex-column gap-3'>
                        {sharedExams.map((item) => {
                          const label = formatCourseLabel(item.semesterCourseName)
                          const statusLabel = statusLabels[item.status] ?? item.status
                          return (
                            <div
                              key={`shared-exam-${item.examId}`}
                              className='border rounded p-3 d-flex flex-column flex-md-row justify-content-between gap-3'
                            >
                              <div>
                                <div className='fw-semibold text-gray-900'>{label}</div>
                                {item.examName && <div className='text-muted small'>Sınav: {item.examName}</div>}
                                <div className='text-muted small'>Durum: {statusLabel}</div>
                              </div>
                              <div className='d-flex align-items-center gap-2'>
                                {item.isSource ? (
                                  <span className='badge badge-light-success'>Kaynak Ders</span>
                                ) : (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-danger'
                                    onClick={() => handleDelete(item)}
                                    disabled={saving}
                                  >
                                    Sil
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>
                Kapat
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSave}
                disabled={saving || queuedCourseIds.length === 0}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' />
    </>
  )
}

export default ManualExamSharedCoursesModal
